/**
 * Typed token bridge for React inline styles.
 *
 * Values live ONLY in `./tokens.css`. This file never hardcodes colors —
 * it only exposes `var(--avatar-*)` so components get autocomplete + type safety.
 *
 * Usage:
 *   import { tokens } from '../theme';
 *   background: tokens.surface.canvas
 *
 * To change a color/spacing/font: edit tokens.css, not this file
 * (unless you are adding a brand-new token name).
 */

type CssVar = `var(--${string})`;

function cssVar(name: `--avatar-${string}`): CssVar {
  return `var(${name})`;
}

export const tokens = {
  surface: {
    canvas: cssVar('--avatar-surface-canvas'),
    elevated: cssVar('--avatar-surface-elevated'),
    input: cssVar('--avatar-surface-input'),
    chip: cssVar('--avatar-surface-chip'),
    danger: cssVar('--avatar-surface-danger'),
    header: cssVar('--avatar-surface-header'),
  },

  text: {
    primary: cssVar('--avatar-text-primary'),
    secondary: cssVar('--avatar-text-secondary'),
    muted: cssVar('--avatar-text-muted'),
    onBrand: cssVar('--avatar-text-on-brand'),
    danger: cssVar('--avatar-text-danger'),
    placeholder: cssVar('--avatar-text-placeholder'),
  },

  border: {
    subtle: cssVar('--avatar-border-subtle'),
    default: cssVar('--avatar-border-default'),
    strong: cssVar('--avatar-border-strong'),
    danger: cssVar('--avatar-border-danger'),
    panel: cssVar('--avatar-border-panel'),
  },

  icon: {
    robotBg: cssVar('--avatar-icon-robot-bg'),
    sparklesBg: cssVar('--avatar-icon-sparkles-bg'),
    targetBg: cssVar('--avatar-icon-target-bg'),
    rocketBg: cssVar('--avatar-icon-rocket-bg'),
    accent: cssVar('--avatar-icon-accent'),
    accentWarm: cssVar('--avatar-icon-accent-warm'),
  },

  gradient: {
    header: cssVar('--avatar-gradient-header'),
    accent: cssVar('--avatar-gradient-accent'),
  },

  focus: {
    ring: cssVar('--avatar-focus-ring'),
  },

  shadow: {
    send: cssVar('--avatar-shadow-send'),
    composer: cssVar('--avatar-shadow-composer'),
  },

  /** Escape hatch for rare primitive needs (prefer semantic tokens above). */
  color: {
    alpha: {
      white18: cssVar('--avatar-alpha-white-18'),
    },
    danger: {
      300: cssVar('--avatar-color-danger-300'),
      700: cssVar('--avatar-color-danger-700'),
    },
  },

  font: {
    family: cssVar('--avatar-font-family'),
    /** Retained Yaro stack — use for brand accents if needed */
    familyBrand: cssVar('--avatar-font-family-brand'),
    size: {
      xs: cssVar('--avatar-font-size-xs'),
      sm: cssVar('--avatar-font-size-sm'),
      md: cssVar('--avatar-font-size-md'),
      lg: cssVar('--avatar-font-size-lg'),
      xl: cssVar('--avatar-font-size-xl'),
    },
    weight: {
      regular: cssVar('--avatar-font-weight-regular'),
      medium: cssVar('--avatar-font-weight-medium'),
      semibold: cssVar('--avatar-font-weight-semibold'),
      bold: cssVar('--avatar-font-weight-bold'),
    },
    lineHeight: {
      tight: cssVar('--avatar-line-height-tight'),
      body: cssVar('--avatar-line-height-body'),
    },
  },

  space: {
    1: cssVar('--avatar-space-1'),
    2: cssVar('--avatar-space-2'),
    3: cssVar('--avatar-space-3'),
    4: cssVar('--avatar-space-4'),
    5: cssVar('--avatar-space-5'),
  },

  radius: {
    sm: cssVar('--avatar-radius-sm'),
    md: cssVar('--avatar-radius-md'),
    lg: cssVar('--avatar-radius-lg'),
    xl: cssVar('--avatar-radius-xl'),
    pill: cssVar('--avatar-radius-pill'),
    bubbleTail: cssVar('--avatar-radius-bubble-tail'),
  },
} as const;

export type Tokens = typeof tokens;
